import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Ban, ChevronDown, ChevronLeft, ChevronRight, MessageCircle, MessageSquare, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EstadoBadge } from '@/components/domain/EstadoBadge';
import { EstadoSelector } from '@/components/domain/EstadoSelector';
import { TransporteBadge } from '@/components/domain/TransporteBadge';
import { ItemDetailDialog } from '@/components/domain/ItemDetailDialog';
import { DesgloseChargeCodes } from '@/components/domain/DesgloseChargeCodes';
import { ComentariosDialog } from '@/components/domain/ComentariosDialog';
import { OpsInfoDialog } from '@/components/domain/OpsInfoDialog';
import { EstadoEnvioSelector } from '@/components/domain/EstadoEnvioSelector';
import { useBookingRealtime } from '@/hooks/useBookingRealtime';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { dueStatus, fmtShortDate, fmtRelative, fmtMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { BookingListItem, Paginated, Item, Estado, ItemNote, DesgloseDetalle, EstadoEnvio } from '@/lib/types';
import { ESTADOS_ENVIO, ESTADO_ENVIO_LABEL, faseToEstadoEnvio } from '@/lib/types';

interface Filters {
  search: string;
  transporte?: string;
  direccion?: string;
  estado?: Estado;
  agenteId?: number;
  serviceLevel?: string;
  estadoEnvio?: EstadoEnvio;
  page: number;
  perPage: number;
}

interface Operativo {
  id: number;
  nombre: string;
  email: string;
  rol: 'agente' | 'gerente' | 'admin';
  activo: boolean;
  bookingCount: number;
}

interface ServiceLevelOption {
  serviceLevel: string;
  count: number;
}

const DEFAULT: Filters = { search: '', page: 1, perPage: 10 };

export function BoardPage() {
  const params = useParams<{ agenteId?: string }>();
  const viewingAgenteId = params.agenteId ? Number(params.agenteId) : undefined;
  const { user } = useAuth();
  // Ya no hay read-only por rol: el backend solo devuelve bookings que el
  // usuario puede editar (admin → todos; gerente → propios + supervisados +
  // delegados; agente → propios + delegados). Por eso todo lo que se muestra
  // es editable; el backend valida en cada PATCH como defensa en profundidad.
  const readOnly = false;
  const [filters, setFilters] = useState<Filters>(DEFAULT);
  const [expandedBookings, setExpandedBookings] = useState<Set<number>>(new Set());
  // ?focus=S26… (desde la campanita): busca el embarque y lo expande con sus
  // tareas del workflow (5-oct-2026).
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get('focus');
  useEffect(() => {
    if (focus) setFilters((f) => ({ ...f, search: focus, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  // Cuando vemos el board de otro agente, traer su info
  const agenteQ = useQuery({
    queryKey: ['agente', viewingAgenteId],
    queryFn: () =>
      api<{ id: number; nombre: string; email: string }>(`/team/agentes/${viewingAgenteId}`),
    enabled: viewingAgenteId != null,
  });

  const bookingsQ = useQuery({
    queryKey: ['bookings', filters, viewingAgenteId],
    queryFn: () =>
      api<Paginated<BookingListItem>>('/bookings', {
        params: {
          page: filters.page,
          perPage: filters.perPage,
          search: filters.search || undefined,
          transporte: filters.transporte,
          direccion: filters.direccion,
          estado: filters.estado,
          serviceLevel: filters.serviceLevel,
          estadoEnvio: filters.estadoEnvio,
          // Si la URL trae /board/:agenteId (gerente viendo a otro), respeta eso;
          // en el board propio, usa el filtro Operativo del toolbar.
          agenteId: viewingAgenteId ?? filters.agenteId,
        },
      }),
  });

  // Lista de operativos disponibles para el filtro (solo cuando no estamos en
  // /board/:agenteId, porque ahí ya estás scoped a uno solo).
  const operativosQ = useQuery({
    queryKey: ['operativos'],
    queryFn: () => api<Operativo[]>('/bookings/operativos'),
    enabled: viewingAgenteId == null,
    staleTime: 60_000,
  });

  // Service Levels disponibles para el filtro
  const serviceLevelsQ = useQuery({
    queryKey: ['service-levels'],
    queryFn: () => api<ServiceLevelOption[]>('/bookings/service-levels'),
    staleTime: 60_000,
  });

  // Conteo de embarques por estado de envío (contadores-filtro del board).
  // Mismos filtros que el listado EXCEPTO estadoEnvio, para que los números
  // coincidan con lo que el board muestra y se muevan al cambiar otros filtros.
  const estadoEnvioCountsQ = useQuery({
    queryKey: [
      'estado-envio-counts',
      viewingAgenteId,
      filters.search,
      filters.transporte,
      filters.direccion,
      filters.estado,
      filters.serviceLevel,
      filters.agenteId,
    ],
    queryFn: () =>
      api<{ totalTodos: number; counts: { estadoEnvio: EstadoEnvio; count: number }[] }>('/bookings/estado-envio-counts', {
        params: {
          search: filters.search || undefined,
          transporte: filters.transporte,
          direccion: filters.direccion,
          estado: filters.estado,
          serviceLevel: filters.serviceLevel,
          agenteId: viewingAgenteId ?? filters.agenteId,
        },
      }),
    staleTime: 30_000,
  });

  // ── Congelar orden de bookings ─────────────────────────────────────────
  // Una vez que la primera respuesta para un conjunto de filtros llega,
  // capturamos el orden de IDs y lo preservamos en re-fetches (que ocurren al
  // marcar/desmarcar tareas). Solo se RECALCULA cuando cambian filtros, page,
  // o viewingAgenteId. Así el embarque no salta al actualizar estados.
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        page: filters.page,
        perPage: filters.perPage,
        search: filters.search,
        transporte: filters.transporte,
        direccion: filters.direccion,
        estado: filters.estado,
        agenteId: filters.agenteId,
        serviceLevel: filters.serviceLevel,
        viewingAgenteId,
      }),
    [filters, viewingAgenteId],
  );
  const [frozenOrder, setFrozenOrder] = useState<{
    key: string;
    ids: number[];
  }>({ key: '', ids: [] });

  useEffect(() => {
    if (!bookingsQ.data?.items) return;
    if (frozenOrder.key !== filterKey) {
      // Filtros cambiaron — capturar nuevo orden del backend (ya viene ordenado
      // por próxima fecha-límite ASC, eta ASC, id ASC).
      setFrozenOrder({
        key: filterKey,
        ids: bookingsQ.data.items.map((b) => b.id),
      });
    } else {
      // Mismos filtros, posiblemente llegó un booking nuevo (raro). Apéndalo.
      const known = new Set(frozenOrder.ids);
      const incoming = bookingsQ.data.items.filter((b) => !known.has(b.id));
      if (incoming.length > 0) {
        setFrozenOrder((prev) => ({
          key: prev.key,
          ids: [...prev.ids, ...incoming.map((b) => b.id)],
        }));
      }
    }
  }, [bookingsQ.data, filterKey, frozenOrder.key, frozenOrder.ids]);

  // Renderizar respetando el orden congelado; los bookings que ya no estén
  // (e.g., completados) se filtran naturalmente porque no vienen en la data.
  const orderedBookings = useMemo(() => {
    if (!bookingsQ.data?.items) return [];
    if (frozenOrder.key !== filterKey || frozenOrder.ids.length === 0) {
      return bookingsQ.data.items;
    }
    const byId = new Map(bookingsQ.data.items.map((b) => [b.id, b]));
    return frozenOrder.ids
      .map((id) => byId.get(id))
      .filter((b): b is BookingListItem => b != null);
  }, [bookingsQ.data, frozenOrder, filterKey]);

  useEffect(() => {
    if (!focus || !bookingsQ.data?.items) return;
    const match = bookingsQ.data.items.find((b) => b.bookingIdExterno === focus);
    if (match) {
      setExpandedBookings((prev) => new Set(prev).add(match.id));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, bookingsQ.data]);

  function toggleExpand(id: number) {
    setExpandedBookings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between">
        <div>
          {viewingAgenteId && (
            <Link
              to="/equipo"
              className="text-xs text-ink-400 hover:text-brand-500 flex items-center gap-1 mb-1"
            >
              <ChevronLeft className="h-3 w-3" /> Equipo
            </Link>
          )}
          <h1 className="text-2xl font-semibold">
            {viewingAgenteId ? `Board · ${agenteQ.data?.nombre ?? '...'}` : 'Mi board'}
          </h1>
          <p className="text-sm text-ink-400 mt-1">
            {bookingsQ.data?.pagination.total ?? '—'} bookings
            {readOnly && <span className="ml-2 text-amber-600">· solo lectura</span>}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-surface-2">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
          <Input
            placeholder="Buscar Booking ID o cliente…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            className="pl-9"
          />
        </div>
        <Select
          value={filters.transporte ?? '__all__'}
          onValueChange={(v) =>
            setFilters({ ...filters, transporte: v === '__all__' ? undefined : v, page: 1 })
          }
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Transporte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los transportes</SelectItem>
            <SelectItem value="SEA">SEA</SelectItem>
            <SelectItem value="AIR">AIR</SelectItem>
            <SelectItem value="ROA">ROA</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.direccion ?? '__all__'}
          onValueChange={(v) =>
            setFilters({ ...filters, direccion: v === '__all__' ? undefined : v, page: 1 })
          }
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Dirección" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Imp + Exp</SelectItem>
            <SelectItem value="Import">Import</SelectItem>
            <SelectItem value="Export">Export</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={
            filters.estado === 'completado'
              ? 'completado'
              : filters.estado === ('cancelado' as Estado)
                ? 'cancelado'
                : filters.estado === ('archivado' as Estado)
                  ? 'archivado'
                  : '__pending__'
          }
          onValueChange={(v) =>
            setFilters({
              ...filters,
              estado:
                v === 'completado' || v === 'cancelado' || v === 'archivado'
                  ? (v as Estado)
                  : undefined,
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__pending__">Pendientes</SelectItem>
            <SelectItem value="completado">Completados</SelectItem>
            {/* Completados con >30 días: los archiva el pipeline automáticamente */}
            <SelectItem value="archivado">Archivados</SelectItem>
            <SelectItem value="cancelado">Cancelados</SelectItem>
          </SelectContent>
        </Select>
        {/* Filtro Operativo: solo se muestra en el board propio
            (en /board/:agenteId ya estás scoped a uno). Si solo hay un
            operativo en el scope, lo ocultamos también para no estorbar. */}
        {viewingAgenteId == null && (operativosQ.data?.length ?? 0) > 1 && (
          <Select
            value={filters.agenteId != null ? String(filters.agenteId) : '__all__'}
            onValueChange={(v) =>
              setFilters({
                ...filters,
                agenteId: v === '__all__' ? undefined : Number(v),
                page: 1,
              })
            }
          >
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Operativo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los operativos</SelectItem>
              {(operativosQ.data ?? []).map((op) => (
                <SelectItem key={op.id} value={String(op.id)}>
                  {op.nombre} ({op.bookingCount})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Filtro Service Level */}
        {(serviceLevelsQ.data?.length ?? 0) > 0 && (
          <Select
            value={filters.serviceLevel ?? '__all__'}
            onValueChange={(v) =>
              setFilters({
                ...filters,
                serviceLevel: v === '__all__' ? undefined : v,
                page: 1,
              })
            }
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Service Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los niveles</SelectItem>
              {(serviceLevelsQ.data ?? []).map((sl) => (
                <SelectItem key={sl.serviceLevel} value={sl.serviceLevel}>
                  {sl.serviceLevel} ({sl.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Contadores-filtro por estado de envío (reemplaza el select "Todos
            los envíos"): cada tile muestra el # de embarques y filtra al click. */}
        <div className="w-full flex flex-wrap items-stretch gap-2 mt-1">
          {(() => {
            const counts = estadoEnvioCountsQ.data?.counts ?? [];
            // "Todos" = TODOS los embarques del alcance (incluye completados
            // y archivados); los demás tiles siguen la vista actual.
            const total = estadoEnvioCountsQ.data?.totalTodos ?? counts.reduce((s, c) => s + c.count, 0);
            const tiles: { key: EstadoEnvio | '__all__'; label: string; count: number }[] = [
              { key: '__all__', label: 'Todos', count: total },
              ...ESTADOS_ENVIO.map((e) => ({
                key: e,
                label: ESTADO_ENVIO_LABEL[e],
                count: counts.find((c) => c.estadoEnvio === e)?.count ?? 0,
              })),
            ];
            const activo = filters.estadoEnvio ?? '__all__';
            return tiles.map((t) => {
              const isActive = activo === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() =>
                    setFilters({
                      ...filters,
                      estadoEnvio: t.key === '__all__' ? undefined : (t.key as EstadoEnvio),
                      page: 1,
                    })
                  }
                  className={cn(
                    'flex flex-col items-center justify-center rounded-md border px-4 py-1.5 min-w-[92px] transition-colors',
                    isActive
                      ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-400'
                      : 'border-ink-200 bg-surface-1 text-ink-600 hover:bg-surface-2',
                  )}
                >
                  <span className="text-lg font-bold leading-none tabular">{t.count}</span>
                  <span className="text-[11px] uppercase tracking-wide mt-0.5">{t.label}</span>
                </button>
              );
            });
          })()}
        </div>
      </div>

      {/* Bookings */}
      {bookingsQ.isLoading && <div className="text-center py-12 text-ink-400">Cargando…</div>}

      {orderedBookings.length === 0 && !bookingsQ.isLoading && (
        <div className="text-center py-12 text-ink-400 border border-dashed border-ink-200 rounded-lg">
          No hay bookings con estos filtros.
        </div>
      )}

      <div className="space-y-3">
        {orderedBookings.map((b) => (
          <BookingGroup
            key={b.id}
            booking={b}
            expanded={expandedBookings.has(b.id)}
            onToggle={() => toggleExpand(b.id)}
            onSelectItem={setSelectedItem}
            readOnly={readOnly}
          />
        ))}
      </div>

      {bookingsQ.data && bookingsQ.data.pagination.total > 0 && (
        <Pagination
          page={bookingsQ.data.pagination.page}
          perPage={bookingsQ.data.pagination.perPage}
          total={bookingsQ.data.pagination.total}
          totalPages={bookingsQ.data.pagination.totalPages}
          onPageChange={(p) => setFilters({ ...filters, page: p })}
          onPerPageChange={(pp) => setFilters({ ...filters, perPage: pp, page: 1 })}
        />
      )}

      {selectedItem != null && (
        <ItemDetailDialog
          itemId={selectedItem}
          open={selectedItem != null}
          onOpenChange={(o) => !o && setSelectedItem(null)}
        />
      )}
    </div>
  );
}

function BookingGroup({
  booking,
  expanded,
  onToggle,
  onSelectItem,
  readOnly,
}: {
  booking: BookingListItem;
  expanded: boolean;
  onToggle: () => void;
  onSelectItem: (id: number) => void;
  readOnly: boolean;
}) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [comentariosOpen, setComentariosOpen] = useState(false);
  const [opsInfoOpen, setOpsInfoOpen] = useState(false);

  // Ocultar desglose y líneas de pendiente (regla de Josué, 09-sep): por lado,
  // cuando el monto es $0 Y su tarea admin está completada; o siempre que el
  // estatus CW sea terminal (CMP/CLS/ARC). Los datos no se tocan, solo la vista.
  const cwTerminal = ['CMP', 'CLS', 'ARC'].includes((booking.cwStatus ?? '').trim().toUpperCase());
  // Avisos de inconsistencia (Josué, 09-sep): booking COMPLETADO pero con
  // estatus CW temprano (INV excluido a propósito) o con pendientes ≠ 0.
  const cwCrudo = (booking.cwStatus ?? '').trim().toUpperCase();
  const completado = booking.estadoProgreso === 'completado';
  const alertaEstatus =
    completado && !!cwCrudo && !['CMP', 'CLS', 'ARC', 'INV'].includes(cwCrudo);
  const alertaPendiente =
    completado &&
    ((booking.pteFacturar != null && booking.pteFacturar !== 0) ||
      (booking.pteProvisionar != null && booking.pteProvisionar !== 0));
  const ocultaFacturar =
    cwTerminal || (booking.pteFacturar === 0 && booking.facturacionCompletada === true);
  const ocultaProvisionar =
    cwTerminal || (booking.pteProvisionar === 0 && booking.posteoCompletado === true);

  return (
    <div className="rounded-lg border border-ink-200 overflow-hidden bg-surface-1">
      <header
        className="px-4 py-3 bg-surface-2 border-b border-brand-500 border-b-2 cursor-pointer hover:bg-ink-100"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <button
              className="p-1 rounded text-ink-400 hover:text-ink-900"
              aria-label="expandir"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <span className="font-semibold tabular">{booking.bookingIdExterno}</span>
            <span className="text-ink-400">·</span>
            <span className="text-ink-900 truncate">{booking.clienteNombre}</span>
            <TransporteBadge
              transporte={booking.transporte}
              modo={booking.modo}
              direccion={booking.direccion}
            />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <EstadoEnvioSelector bookingId={booking.id} value={booking.estadoEnvio} />
            {booking.serviceLevel && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700"
                title="Service Level"
              >
                {booking.serviceLevel}
              </span>
            )}
            {booking.cwStatus && (
              <span className="text-xs flex items-center gap-1">
                <span className="text-ink-400">CW:</span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
                  {booking.cwStatus}
                </span>
              </span>
            )}
            {booking.cancelado ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-700">
                ⚠ Cancelado
              </span>
            ) : (
              <EstadoBadge estado={booking.estadoProgreso} />
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpsInfoOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs text-ink-600 hover:text-brand-700 hover:bg-surface-1 px-2 py-0.5 rounded"
              title="Información adicional del embarque (Reportes·Operaciones)"
            >
              <Search className="h-3 w-3" />
              Info
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotesOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs text-ink-600 hover:text-brand-700 hover:bg-surface-1 px-2 py-0.5 rounded"
              title="Ver notas de las subtareas"
            >
              <MessageSquare className="h-3 w-3" />
              Notas
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setComentariosOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs text-ink-600 hover:text-brand-700 hover:bg-surface-1 px-2 py-0.5 rounded"
              title="Comentarios del reporte de Bookings"
            >
              <MessageCircle className="h-3 w-3" />
              Comentarios{booking._count.comentarios > 0 ? ` (${booking._count.comentarios})` : ''}
            </button>
            <span className="text-xs text-ink-400 tabular">
              {booking._count.items} items
            </span>
          </div>
        </div>
        <div className="mt-1 text-xs text-ink-400 ml-7">
          Operativo:{' '}
          <span className="text-ink-900 font-medium">{booking.agente.nombre}</span> ·{' '}
          {booking.comercialNombre && (
            <>
              Comercial: <span className="text-ink-900">{booking.comercialNombre}</span> ·{' '}
            </>
          )}
          ETD <span className="tabular">{fmtShortDate(booking.etd)}</span> · ETA{' '}
          <span className="tabular">{fmtShortDate(booking.eta)}</span> · Recibido{' '}
          <span className="tabular">{fmtShortDate(booking.fechaRecepcion)}</span>
          {booking.proximaFechaLimite && (() => {
            const due = dueStatus(booking.proximaFechaLimite);
            const isOverdue = due === 'vencida';
            const isToday = due === 'hoy';
            return (
              <>
                {' · '}
                <span
                  className={cn(
                    'tabular font-medium',
                    isOverdue && 'text-brand-700',
                    isToday && 'text-brand-500',
                    !isOverdue && !isToday && 'text-ink-900',
                  )}
                >
                  {isOverdue && '⚠ Tarea vencida desde '}
                  {isToday && 'Tarea vence hoy'}
                  {!isOverdue && !isToday && 'Próxima tarea vence '}
                  {!isToday && fmtShortDate(booking.proximaFechaLimite)}
                </span>
              </>
            );
          })()}
        </div>
        {/* Vista rápida de Reportes·Operaciones (Shipment Profile) */}
        {booking.opsInfo &&
          (booking.opsInfo.master ||
            booking.opsInfo.houseRef ||
            booking.opsInfo.origenPuerto ||
            booking.opsInfo.vesselFlight ||
            booking.opsInfo.linea) && (
            <div className="mt-0.5 text-xs text-ink-400 ml-7 truncate" title="Información de operaciones (botón Info para el detalle)">
              {booking.opsInfo.master && (
                <>
                  {booking.transporte?.toUpperCase() === 'ROA' ? 'BOL' : 'MBL'}:{' '}
                  <span className="text-ink-600 tabular">{booking.opsInfo.master}</span> ·{' '}
                </>
              )}
              {booking.opsInfo.houseRef && booking.transporte?.toUpperCase() !== 'ROA' && (
                <>
                  HBL: <span className="text-ink-600 tabular">{booking.opsInfo.houseRef}</span> ·{' '}
                </>
              )}
              {booking.opsInfo.origenPuerto && booking.opsInfo.destinoPuerto && (
                <>
                  <span className="text-ink-600 tabular">
                    {booking.opsInfo.origenPuerto}→{booking.opsInfo.destinoPuerto}
                  </span>{' '}
                  ·{' '}
                </>
              )}
              {booking.opsInfo.container && (
                <>
                  Cont: <span className="text-ink-600">{booking.opsInfo.container}</span> ·{' '}
                </>
              )}
              {booking.opsInfo.vesselFlight && (
                <>
                  <span className="text-ink-600">{booking.opsInfo.vesselFlight}</span> ·{' '}
                </>
              )}
              {booking.opsInfo.linea && <span className="text-ink-600">{booking.opsInfo.linea}</span>}
            </div>
          )}
        {/* Facturación / provisión — una fila por concepto: total USD, límite y desglose */}
        {!ocultaFacturar && (booking.pteFacturar != null || booking.limiteFacturar || booking.desgloseFacturar) && (
          <div className="mt-1 text-xs text-ink-400 ml-7">
            Pte. facturar:{' '}
            {booking.pteFacturar != null && (
              <span className="tabular text-ink-900 font-medium">{fmtMoney(booking.pteFacturar)} USD</span>
            )}
            {booking.limiteFacturar && (
              <span className="text-ink-400"> (límite {fmtShortDate(booking.limiteFacturar)})</span>
            )}
            {/* Resumen del header: horizontal, solo código (sin descripción) */}
            {booking.desgloseFacturar && (
              <>
                {' '}<span className="text-ink-600 font-medium">Desglose:</span>{' '}
                <span className="text-ink-500 tabular">{booking.desgloseFacturar}</span>
              </>
            )}
          </div>
        )}
        {!ocultaProvisionar && (booking.pteProvisionar != null || booking.limiteProvisionar || booking.desgloseProvisionar) && (
          <div className="mt-0.5 text-xs text-ink-400 ml-7">
            Pte. provisionar:{' '}
            {booking.pteProvisionar != null && (
              <span className="tabular text-ink-900 font-medium">{fmtMoney(booking.pteProvisionar)} USD</span>
            )}
            {booking.limiteProvisionar && (
              <span className="text-ink-400"> (límite {fmtShortDate(booking.limiteProvisionar)})</span>
            )}
            {booking.desgloseProvisionar && (
              <>
                {' '}<span className="text-ink-600 font-medium">Desglose:</span>{' '}
                <span className="text-ink-500 tabular">{booking.desgloseProvisionar}</span>
              </>
            )}
          </div>
        )}
        {(alertaEstatus || alertaPendiente) && (
          <div className="mt-1.5 ml-7 flex flex-wrap gap-2">
            {alertaEstatus && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 border border-red-200 px-2 py-1 text-xs font-semibold text-red-700">
                ⚠ Completado, pero el estatus en CW es {cwCrudo} (≠ CMP/CLS)
              </span>
            )}
            {alertaPendiente && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 border border-red-200 px-2 py-1 text-xs font-semibold text-red-700">
                ⚠ Completado con pendiente por
                {booking.pteFacturar != null && booking.pteFacturar !== 0
                  ? ` facturar ${fmtMoney(booking.pteFacturar)}`
                  : ''}
                {booking.pteFacturar && booking.pteProvisionar ? ' y' : ''}
                {booking.pteProvisionar != null && booking.pteProvisionar !== 0
                  ? ` provisionar ${fmtMoney(booking.pteProvisionar)}`
                  : ''}
              </span>
            )}
          </div>
        )}
        {/* Detención de carga + comentario del reporte de Bookings */}
        {booking.detenido && (
          <div className="mt-1.5 ml-7 inline-flex items-center gap-1.5 rounded-md bg-red-50 border border-red-200 px-2 py-1 text-xs font-semibold text-red-700">
            <Ban className="h-3.5 w-3.5 shrink-0" />
            Detenido
            {booking.detenidoAt && (
              <span className="font-normal text-red-600">· desde {fmtShortDate(booking.detenidoAt)}</span>
            )}
            {booking.detenidoPor && (
              <span className="font-normal text-red-500">· por {booking.detenidoPor.split('@')[0]}</span>
            )}
          </div>
        )}
      </header>
      {expanded && (
        <BookingItems
          bookingId={booking.id}
          bookingAgenteId={booking.agente.id}
          onSelectItem={onSelectItem}
          readOnly={readOnly}
          desgloseFacturar={ocultaFacturar ? null : booking.desgloseFacturar}
          desgloseProvisionar={ocultaProvisionar ? null : booking.desgloseProvisionar}
          desgloseFacturarDetalle={ocultaFacturar ? null : booking.desgloseFacturarDetalle}
          desgloseProvisionarDetalle={ocultaProvisionar ? null : booking.desgloseProvisionarDetalle}
          bookingEstadoEnvio={booking.estadoEnvio}
        />
      )}
      <OpsInfoDialog
        bookingId={booking.id}
        bookingIdExterno={booking.bookingIdExterno}
        transporte={booking.transporte}
        open={opsInfoOpen}
        onOpenChange={setOpsInfoOpen}
      />
      <BookingNotesDialog
        open={notesOpen}
        onOpenChange={setNotesOpen}
        booking={booking}
      />
      <ComentariosDialog
        open={comentariosOpen}
        onOpenChange={setComentariosOpen}
        bookingId={booking.id}
        bookingIdExterno={booking.bookingIdExterno}
        cliente={booking.clienteNombre}
        readOnly={readOnly}
      />
    </div>
  );
}

// ─── Dialog: timeline de TODAS las notas de un booking ────────────────────
function BookingNotesDialog({
  open,
  onOpenChange,
  booking,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  booking: BookingListItem;
}) {
  const notesQ = useQuery({
    queryKey: ['booking-notes', booking.id],
    queryFn: () => api<{ notes: ItemNote[] }>(`/bookings/${booking.id}/notes`),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Notas del booking {booking.bookingIdExterno}
          </DialogTitle>
          <DialogDescription>
            {booking.clienteNombre} · Timeline completo (append-only). Más reciente arriba.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {notesQ.isLoading && <p className="text-sm text-ink-400">Cargando…</p>}
          {!notesQ.isLoading && (notesQ.data?.notes?.length ?? 0) === 0 && (
            <p className="text-sm text-ink-400 italic">
              Ningún agente ha agregado notas a las subtareas de este booking todavía.
            </p>
          )}
          {notesQ.data?.notes?.map((n) => (
            <div
              key={n.id}
              className="border-l-2 border-amber-400 pl-3 py-1.5 bg-amber-50/40 rounded-r"
            >
              {n.item && (
                <div className="text-[11px] text-ink-500 mb-0.5">
                  <span className="font-mono">{n.item.hierarchyId}</span>{' '}
                  <span className="text-ink-700">{n.item.nombre}</span>
                  {n.item.fase && (
                    <span className="ml-1 inline-block px-1.5 py-0 rounded bg-ink-100 text-ink-600 text-[10px]">
                      {n.item.fase}
                    </span>
                  )}
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap text-ink-900">{n.texto}</p>
              <div className="text-[11px] text-ink-400 mt-1">
                por <span className="font-medium text-ink-700">{n.user.nombre}</span> ·{' '}
                {fmtRelative(n.createdAt)}{' '}
                <span className="text-ink-300">
                  ({new Date(n.createdAt).toLocaleString('es-MX', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })})
                </span>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookingItems({
  bookingId,
  bookingAgenteId,
  onSelectItem,
  readOnly,
  desgloseFacturar,
  desgloseProvisionar,
  desgloseFacturarDetalle,
  desgloseProvisionarDetalle,
  bookingEstadoEnvio,
}: {
  bookingId: number;
  bookingAgenteId: number;
  onSelectItem: (id: number) => void;
  readOnly: boolean;
  desgloseFacturar?: string | null;
  desgloseProvisionar?: string | null;
  desgloseFacturarDetalle?: DesgloseDetalle[] | null;
  desgloseProvisionarDetalle?: DesgloseDetalle[] | null;
  bookingEstadoEnvio?: EstadoEnvio | null;
}) {
  // Desglose de charge codes junto a las tareas de facturación/provisión.
  // El número de tarea varía por booking (11.1, 20.1, 28.1…), así que el
  // match es por nombre. Si el booking no tiene desglose (sin pendientes),
  // no se muestra nada.
  const desgloseForItem = (it: Item): string | null => {
    const nombre = (it.nombre ?? '').trim().toLowerCase();
    if (nombre === 'facturación a cliente') return desgloseFacturar ?? null;
    if (nombre === 'posteo de facturas del agente o proveedor') return desgloseProvisionar ?? null;
    return null;
  };
  const desgloseDetalleForItem = (it: Item): DesgloseDetalle[] | null => {
    const nombre = (it.nombre ?? '').trim().toLowerCase();
    if (nombre === 'facturación a cliente') return desgloseFacturarDetalle ?? null;
    if (nombre === 'posteo de facturas del agente o proveedor') return desgloseProvisionarDetalle ?? null;
    return null;
  };
  const { user } = useAuth();
  // El usuario puede editar si es admin O el agente de este booking
  // Editable si: admin, gerente (el backend ya limitó el scope a sus
  // supervisados/propios), o el agente dueño del booking. El backend valida
  // en cada PATCH, así que esto solo controla si el checkbox es interactivo.
  const canEdit =
    !!user && (user.rol === 'admin' || user.rol === 'director' || user.rol === 'gerente' || user.id === bookingAgenteId);
  // Suscribirse a cambios realtime de este booking
  useBookingRealtime(bookingId);

  const { data, isLoading } = useQuery({
    queryKey: ['booking-items', bookingId],
    queryFn: () =>
      api<{ items: (Item & { _count: { children: number } })[] }>(`/bookings/${bookingId}/items`),
  });

  // Mapa id → item para lookup de ancestros
  const byId = useMemo(() => {
    const map = new Map<number, Item & { _count: { children: number } }>();
    data?.items.forEach((it) => map.set(it.id, it));
    return map;
  }, [data]);

  // Estado de colapso por item (para ítems no-hoja).
  // Inicial: colapsa todos los completados + nivel-1 no-completados EXCEPTO el primero.
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized || !data?.items?.length) return;
    const items = data.items;
    const next = new Set<number>();

    // Regla: el colapso automático solo se aplica al NIVEL-1 (las "tareas"
    // tipo Análisis, Booking, etc.). Una vez abierta una tarea, todos sus
    // subniveles se ven recursivamente — el usuario puede colapsar manual.
    //
    //  - Todos los nivel-1 con hijos arrancan colapsados…
    //  - …excepto el PRIMER nivel-1 no completado (la "tarea en curso"),
    //    que se deja expandido.
    const level1WithChildren = items.filter((it) => it.level === 1 && it._count.children > 0);
    for (const it of level1WithChildren) next.add(it.id);

    const firstActive = level1WithChildren.find((it) => it.estado !== 'completado');
    if (firstActive) next.delete(firstActive.id);

    setCollapsed(next);
    setInitialized(true);
  }, [data, initialized]);

  // Fecha límite EFECTIVA por item: para PADRES se muestra la más LEJANA de
  // su subárbol (una actividad no vence hasta que vence su última subtarea);
  // para hojas, la propia. Fallback a la propia si el subárbol no trae fechas.
  const fechaEfectiva = useMemo(() => {
    const items = data?.items ?? [];
    const hijos = new Map<number, typeof items>();
    for (const it of items) {
      if (it.parentId != null) {
        const arr = hijos.get(it.parentId) ?? [];
        arr.push(it);
        hijos.set(it.parentId, arr);
      }
    }
    const map = new Map<number, string | null>();
    const resolve = (it: (typeof items)[number]): string | null => {
      const cached = map.get(it.id);
      if (cached !== undefined) return cached;
      let best: string | null = it._count.children === 0 ? (it.fechaLimite ?? null) : null;
      for (const k of hijos.get(it.id) ?? []) {
        const f = resolve(k);
        if (f && (!best || f > best)) best = f; // ISO: comparación lexicográfica válida
      }
      if (!best) best = it.fechaLimite ?? null;
      map.set(it.id, best);
      return best;
    };
    items.forEach(resolve);
    return map;
  }, [data]);

  // ¿El item es visible? Sí, salvo que algún ancestro esté colapsado.
  const isVisible = (item: Item & { _count: { children: number } }): boolean => {
    let parentId = item.parentId;
    while (parentId != null) {
      if (collapsed.has(parentId)) return false;
      const parent = byId.get(parentId);
      if (!parent) break;
      parentId = parent.parentId;
    }
    return true;
  };

  const toggleCollapse = (itemId: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => {
    if (!data?.items) return;
    const next = new Set<number>();
    for (const it of data.items) {
      if (it._count.children > 0) next.add(it.id);
    }
    setCollapsed(next);
  };

  if (isLoading) return <div className="p-6 text-center text-ink-400">Cargando items…</div>;

  // Conteo descendiente para mostrar "(X)" junto a items colapsados
  const descendantCount = (parentId: number): number => {
    let count = 0;
    const stack = [parentId];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      for (const it of data?.items ?? []) {
        if (it.parentId === cur) {
          count++;
          stack.push(it.id);
        }
      }
    }
    return count;
  };

  return (
    <>
      <div className="px-4 py-1.5 border-b border-ink-100 bg-surface-1 flex items-center gap-2 text-xs">
        <button
          onClick={expandAll}
          className="text-ink-400 hover:text-ink-900 underline-offset-2 hover:underline"
        >
          Expandir todo
        </button>
        <span className="text-ink-300">·</span>
        <button
          onClick={collapseAll}
          className="text-ink-400 hover:text-ink-900 underline-offset-2 hover:underline"
        >
          Colapsar todo
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-surface-1">
          <tr className="text-xs uppercase tracking-wider text-ink-400 border-b border-ink-100">
            <th className="px-2 py-2 text-left font-medium w-8"></th>
            <th className="px-3 py-2 text-left font-medium">Elemento</th>
            <th className="px-3 py-2 text-left font-medium w-36">Estado</th>
            <th className="px-3 py-2 text-left font-medium w-32">Fase</th>
            <th className="px-3 py-2 text-left font-medium w-24 tabular">Vence</th>
            <th className="px-3 py-2 text-right font-medium w-16"></th>
          </tr>
        </thead>
        <tbody>
          {data?.items.map((it) => {
            if (!isVisible(it)) return null;

            const indent = (it.level - 1) * 16;
            const isLeaf = it._count.children === 0;
            const isCollapsed = collapsed.has(it.id);
            const childCount = !isLeaf ? descendantCount(it.id) : 0;
            // Segmentación por etapa (decisión de Fer, 28-ago): las tareas de
            // OTRA etapa se atenúan pero siguen palomeables. Administrativo y
            // fases desconocidas mapean a null → nunca se atenúan.
            const etapaTarea = faseToEstadoEnvio(it.fase);
            const otraEtapa =
              !!bookingEstadoEnvio && !!etapaTarea && etapaTarea !== bookingEstadoEnvio;

            return (
              <tr
                key={it.id}
                className={cn(
                  'border-b border-ink-100 hover:bg-surface-2 cursor-pointer',
                  !isLeaf && 'font-medium bg-surface-1/40',
                  otraEtapa && 'opacity-45 hover:opacity-90 transition-opacity',
                )}
                title={otraEtapa ? 'Tarea de otra etapa del embarque (se puede trabajar igual)' : undefined}
                onClick={() => onSelectItem(it.id)}
              >
                <td className="px-2 py-1.5 align-middle">
                  {!isLeaf ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCollapse(it.id);
                      }}
                      className="p-1 rounded text-ink-400 hover:text-ink-900 hover:bg-ink-100"
                      aria-label={isCollapsed ? 'Expandir' : 'Colapsar'}
                      title={isCollapsed ? 'Expandir' : 'Colapsar'}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                  ) : null}
                </td>
                <td className="px-3 py-1.5">
                  <div style={{ paddingLeft: indent }}>
                    <div className="flex items-baseline gap-2">
                      <span className="tabular text-xs text-ink-400 shrink-0">{it.hierarchyId}</span>
                      <span className="line-clamp-1">{it.nombre}</span>
                      {!isLeaf && isCollapsed && childCount > 0 && (
                        <span className="text-[11px] text-ink-400 italic">
                          ({childCount} oculta{childCount === 1 ? '' : 's'})
                        </span>
                      )}
                    </div>
                    {(desgloseDetalleForItem(it) || desgloseForItem(it)) && (
                      <div className="text-xs ml-6 mt-0.5">
                        <DesgloseChargeCodes
                          detalle={desgloseDetalleForItem(it)}
                          fallback={desgloseForItem(it)}
                        />
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <EstadoSelector
                    itemId={it.id}
                    estado={it.estado}
                    isLeaf={isLeaf}
                    canEdit={canEdit}
                    readOnly={readOnly}
                  />
                </td>
                <td className="px-3 py-1.5 text-xs text-ink-400">{it.fase ?? '—'}</td>
                <td
                  className={cn(
                    'px-3 py-1.5 text-xs tabular',
                    (() => {
                      // Padres: vigencia = subtarea con fecha MÁS LEJANA del subárbol
                      const fl = fechaEfectiva.get(it.id) ?? it.fechaLimite;
                      // Solo destacamos vencidas/hoy si el ítem no está completado
                      if (it.estado === 'completado') return 'text-ink-300 line-through';
                      const due = dueStatus(fl);
                      if (due === 'vencida') return 'text-brand-700 font-semibold';
                      if (due === 'hoy') return 'text-brand-500 font-semibold';
                      return 'text-ink-400';
                    })(),
                  )}
                >
                  {(() => {
                    const fl = fechaEfectiva.get(it.id) ?? it.fechaLimite;
                    if (it.estado !== 'completado') {
                      const due = dueStatus(fl);
                      if (due === 'vencida') return `⚠ ${fmtShortDate(fl)}`;
                      if (due === 'hoy') return `Hoy · ${fmtShortDate(fl)}`;
                    }
                    return fmtShortDate(fl);
                  })()}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-ink-300">
                  {it._count.children > 0 && `${it._count.children}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function Pagination({
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
}: {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onPerPageChange: (pp: number) => void;
}) {
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
      <div className="text-sm text-ink-400 tabular">
        Mostrando <span className="text-ink-900 font-medium">{start}-{end}</span> de{' '}
        <span className="text-ink-900 font-medium">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>◀</Button>
        <span className="text-sm tabular px-2">{page} / {totalPages}</span>
        <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>▶</Button>
        <Select value={String(perPage)} onValueChange={(v) => onPerPageChange(Number(v))}>
          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="20">20</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
